import React, { FunctionComponent, useState } from 'react'
import { Line } from 'react-chartjs-2'
import { useTranslation } from 'react-i18next'

import { IDayResult } from '../../Client/IResult'

import { IProps } from './IProps'
import style from './style.scss'

const Chart: FunctionComponent<IProps> = ({ result1, result2, span }) => {
    const { t } = useTranslation()

    const dayLabels: string[] = [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday',
    ].map((value) => t(value))

    const yearLabels = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
    ].map((value) => t(value))

    const maxMonthLabels = new Array<number>(31)
        .fill(1)
        .map((value, index) => value + index)

    const dayResults1: IDayResult[] = result1.dayResults
    const dayResults2: IDayResult[] | undefined = result2?.dayResults

    const getLabel = (dayResults: IDayResult[]): undefined | string => {
        if (!dayResults[0]) {
            return t('No data')
        }

        if (span === 'month') {
            return dayResults[0]?.date.format('MMMM')
        }

        if (span === 'year') {
            return dayResults[0]?.date.format('YYYY')
        }

        return t('Week ') + dayResults[0]?.date.format('W')
    }

    const getData = (dayResults: IDayResult[]) => {
        if (span === 'month') {
            return maxMonthLabels.map((dayNumber: number) => {
                return dayResults
                    .filter((result) => result.date.date() === dayNumber)
                    .map((result) => result.total)
                    .reduce((a, b) => a + b, 0)
                    .toString()
            })
        }

        if (span === 'year') {
            return yearLabels.map((month: string) => {
                return dayResults
                    .filter((result) => result.date.format('MMMM') === month)
                    .map((result) => result.total)
                    .reduce((a, b) => a + b, 0)
                    .toString()
            })
        }

        return dayLabels.map((dayName): number => {
            const value = dayResults.find(
                (result) => result.date.format('dddd') === dayName
            )

            return value ? value.total : 0
        })
    }

    const getDataset = () => {
        const datasets = [
            {
                label: getLabel(dayResults1),
                backgroundColor: '#005cdb',
                borderColor: '#005cdb',
                data: getData(dayResults1),
                fill: false,
                pointRadius: 5,
                pointHoverRadius: 10,
            },
        ]

        if (dayResults2) {
            datasets.push({
                label: getLabel(dayResults2),
                backgroundColor: '#FFA500',
                borderColor: '#FFA500',
                data: getData(dayResults2),
                fill: false,
                pointRadius: 5,
                pointHoverRadius: 10,
            })
        }

        return datasets
    }

    const data = {
        labels:
            span === 'month'
                ? maxMonthLabels.map((month) => month.toString())
                : span === 'year'
                ? yearLabels
                : dayLabels,
        datasets: getDataset(),
    }

    const options = {
        responsive: true,
        elements: {
            point: { pointStyle: 'rectRounded' },
        },
    }

    return (
        <div className={style.graphContainer}>
            <Line data={data} options={options} height={100} />
        </div>
    )
}

export default Chart
